import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL,

  prepareHeaders: (headers, { getState }) => {
    console.log('Base URL:', process.env.REACT_APP_API_URL);
    const token = getState().user.accessToken;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  return result;
};

// ── Services API ──────────────────────────────────────────────────────────────
export const ServicesAPI = createApi({
  reducerPath: 'ServicesAPI',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Service'],
  endpoints: (builder) => ({

    getServices: builder.query({
      query: () => '/api/services/',
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Service', id })), { type: 'Service', id: 'LIST' }]
          : [{ type: 'Service', id: 'LIST' }],
    }),

    getServiceById: builder.query({
      query: (id) => `/api/services/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Service', id }],
    }),

    createService: builder.mutation({
      query: (data) => ({ url: '/api/services/', method: 'POST', body: data }),
      invalidatesTags: [{ type: 'Service', id: 'LIST' }],
    }),

    updateService: builder.mutation({
      query: ({ id, ...data }) => ({ url: `/api/services/${id}/`, method: 'PUT', body: data }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Service', id }],
    }),

    patchService: builder.mutation({
      query: ({ id, ...data }) => ({ url: `/api/services/${id}/`, method: 'PATCH', body: data }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Service', id }],
    }),

    deleteService: builder.mutation({
      query: (id) => ({ url: `/api/services/${id}/`, method: 'DELETE' }),
      invalidatesTags: (result, error, id) => [
        { type: 'Service', id },
        { type: 'Service', id: 'LIST' },
      ],
    }),

  }),
});

// ── Bookings API ──────────────────────────────────────────────────────────────
export const BookingsAPI = createApi({
  reducerPath: 'BookingsAPI',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Booking'],
  endpoints: (builder) => ({

    getBookings: builder.query({
      query: (params = {}) => ({ url: '/api/bookings/', params }),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Booking', id })), { type: 'Booking', id: 'LIST' }]
          : [{ type: 'Booking', id: 'LIST' }],
    }),

    getBookingById: builder.query({
      query: (id) => `/api/bookings/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Booking', id }],
    }),

    createBooking: builder.mutation({
      query: (data) => ({ url: '/api/bookings/', method: 'POST', body: data }),
      invalidatesTags: [{ type: 'Booking', id: 'LIST' }],
    }),

    updateBooking: builder.mutation({
      query: ({ id, ...data }) => ({ url: `/api/bookings/${id}/`, method: 'PUT', body: data }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Booking', id }],
    }),

    patchBooking: builder.mutation({
      query: ({ id, ...data }) => ({ url: `/api/bookings/${id}/`, method: 'PATCH', body: data }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Booking', id },
        { type: 'Booking', id: 'LIST' },
      ],
    }),

    deleteBooking: builder.mutation({
      query: (id) => ({ url: `/api/bookings/${id}/`, method: 'DELETE' }),
      invalidatesTags: (result, error, id) => [
        { type: 'Booking', id },
        { type: 'Booking', id: 'LIST' },
      ],
    }),

  }),
});

// ── Products API ──────────────────────────────────────────────────────────────
export const ProductsAPI = createApi({
  reducerPath: 'ProductsAPI',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Product'],
  endpoints: (builder) => ({

    // GET /api/products/
    getProducts: builder.query({
      query: () => '/api/products/',
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Product', id })), { type: 'Product', id: 'LIST' }]
          : [{ type: 'Product', id: 'LIST' }],
    }),

    // GET /api/products/<id>/
    getProductById: builder.query({
      query: (id) => `/api/products/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Product', id }],
    }),

    // POST /api/products/ — multipart FormData for image upload
    createProduct: builder.mutation({
      query: (formData) => ({
        url: '/api/products/',
        method: 'POST',
        body: formData,         // FormData — RTK Query will NOT set Content-Type, letting browser set it with boundary
      }),
      invalidatesTags: [{ type: 'Product', id: 'LIST' }],
    }),

    // PUT /api/products/<id>/
    updateProduct: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/api/products/${id}/`,
        method: 'PUT',
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Product', id },
        { type: 'Product', id: 'LIST' },
      ],
    }),

    // PATCH /api/products/<id>/
    patchProduct: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/api/products/${id}/`,
        method: 'PATCH',
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Product', id },
        { type: 'Product', id: 'LIST' },
      ],
    }),

    // DELETE /api/products/<id>/
    deleteProduct: builder.mutation({
      query: (id) => ({ url: `/api/products/${id}/`, method: 'DELETE' }),
      invalidatesTags: (result, error, id) => [
        { type: 'Product', id },
        { type: 'Product', id: 'LIST' },
      ],
    }),

  }),
});

// ── Projects API ──────────────────────────────────────────────────────────────
export const ProjectsAPI = createApi({
  reducerPath: 'ProjectsAPI',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Project'],
  endpoints: (builder) => ({

    // GET /api/projects/
    getProjects: builder.query({
      query: () => '/api/services/projects/',
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Project', id })), { type: 'Project', id: 'LIST' }]
          : [{ type: 'Project', id: 'LIST' }],
    }),

    // GET /api/projects/<id>/
    getProjectById: builder.query({
      query: (id) => `/api/services/projects/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Project', id }],
    }),

    // POST /api/projects/ — multipart FormData for image upload
    createProject: builder.mutation({
      query: (formData) => ({
        url: '/api/services/projects/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: [{ type: 'Project', id: 'LIST' }],
    }),

    // PUT /api/projects/<id>/
    updateProject: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/api/services/projects/${id}/`,
        method: 'PUT',
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Project', id },
        { type: 'Project', id: 'LIST' },
      ],
    }),

    // PATCH /api/projects/<id>/
    patchProject: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/api/services/projects/${id}/`,
        method: 'PATCH',
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Project', id },
        { type: 'Project', id: 'LIST' },
      ],
    }),

    // DELETE /api/projects/<id>/
    deleteProject: builder.mutation({
      query: (id) => ({ url: `/api/services/projects/${id}/`, method: 'DELETE' }),
      invalidatesTags: (result, error, id) => [
        { type: 'Project', id },
        { type: 'Project', id: 'LIST' },
      ],
    }),

  }),
});


export const {
  useGetServicesQuery,
  useGetServiceByIdQuery,
  useCreateServiceMutation,
  useUpdateServiceMutation,
  usePatchServiceMutation,
  useDeleteServiceMutation,
} = ServicesAPI;

export const {
  useGetBookingsQuery,
  useGetBookingByIdQuery,
  useCreateBookingMutation,
  useUpdateBookingMutation,
  usePatchBookingMutation,
  useDeleteBookingMutation,
} = BookingsAPI;

export const {
  useGetProductsQuery,
  useGetProductByIdQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  usePatchProductMutation,
  useDeleteProductMutation,
} = ProductsAPI;

export const {
  useGetProjectsQuery,
  useGetProjectByIdQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  usePatchProjectMutation,
  useDeleteProjectMutation,
} = ProjectsAPI;
